import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import useSeo from "@/lib/useSeo";
import RolesHiringStrip from "@/components/RolesHiringStrip";
import {
    MagnifyingGlass,
    MapPin,
    Buildings,
    ArrowRight,
    TrendUp,
    CheckCircle,
    Sparkle,
    Lightbulb,
    ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CITIES = [
    "Bengaluru", "Mumbai", "Delhi", "Gurugram", "Noida", "Pune",
    "Hyderabad", "Chennai", "Kolkata", "Ahmedabad", "Kochi", "Remote",
];

const fmtINR = (n) => {
    if (!n) return "—";
    if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(2)} Cr`;
    if (n >= 100_000) return `₹${(n / 100_000).toFixed(1)} L`;
    return `₹${(n / 1000).toFixed(0)}k`;
};

// Small percentile-bar visual. Range from p25 to p90; markers at each stop.
function PercentileBar({ p25, p50, p75, p90 }) {
    const min = p25;
    const max = p90;
    const pct = (v) => {
        if (max === min) return 0;
        return Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
    };
    return (
        <div className="relative h-14 mt-6">
            <div className="absolute left-0 right-0 top-6 h-2 bg-gradient-to-r from-emerald-400 via-cobalt to-splash rounded-full" />
            {[
                { v: p25, label: "P25" },
                { v: p50, label: "Median" },
                { v: p75, label: "P75" },
                { v: p90, label: "Top 10%" },
            ].map((m) => (
                <div
                    key={m.label}
                    className="absolute -translate-x-1/2"
                    style={{ left: `${pct(m.v)}%` }}
                >
                    <div className="w-0.5 h-4 bg-ink mx-auto" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mt-1 whitespace-nowrap">
                        {m.label}
                    </p>
                    <p className="text-[11px] font-heading font-black text-ink whitespace-nowrap">
                        {fmtINR(m.v)}
                    </p>
                </div>
            ))}
        </div>
    );
}

export default function SalaryBenchmarkPage() {
    useSeo({
        title: "Free Salary Benchmark · India · P25/P50/P75/P90 | NaukriDaddy",
        description: "Free AI-powered salary benchmark for the Indian job market. P25, median, P75 & top-10% pay bands for any role, years-of-experience, and city — with a package breakdown.",
        canonical: "https://naukridaddy.com/salary-benchmark",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "NaukriDaddy Salary Benchmark",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
            "description": "Free salary calculator for the Indian job market. Returns P25, median, P75 and top-10% bands across 12+ cities and 25+ roles.",
            "provider": {
                "@type": "Organization",
                "name": "Naukridaddy HR Solutions Private Limited",
                "url": "https://naukridaddy.com",
            },
        },
    });
    const [roles, setRoles] = useState([]);
    const [role, setRole] = useState("");
    const [years, setYears] = useState(3);
    const [city, setCity] = useState("Bengaluru");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [email, setEmail] = useState("");
    const [leadCaptured, setLeadCaptured] = useState(false);
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);

    useEffect(() => {
        api.get("/salary/roles").then((r) => setRoles(r.data.roles || [])).catch(() => setRoles([]));
    }, []);

    const filteredRoles = useMemo(() => {
        const q = role.trim().toLowerCase();
        if (!q) return roles.slice(0, 8);
        return roles.filter((r) => r.label.toLowerCase().includes(q) || r.key.includes(q)).slice(0, 8);
    }, [role, roles]);

    const submit = async (e) => {
        e?.preventDefault();
        if (!role.trim()) {
            toast.error("Type your role");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/salary/estimate", {
                role: role.trim(), years: Number(years) || 0, city: city.trim() || "Bengaluru",
            });
            setResult(data);
            setLeadCaptured(false);
            setTimeout(() => {
                document.getElementById("salary-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't estimate — try a closer role title");
        } finally {
            setLoading(false);
        }
    };

    const submitLead = async (e) => {
        e?.preventDefault();
        if (!email.trim()) {
            toast.error("Enter your email");
            return;
        }
        try {
            await api.post("/salary/lead", {
                email: email.trim(), role: result?.role?.label, city: result?.city,
                years: result?.years, consent: true,
            });
            setLeadCaptured(true);
            toast.success("You're in. Weekly salary trends coming your way.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save your email");
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="salary-benchmark-page">
            <PublicHeader currentPath="/salary-benchmark" />

            {/* Hero + form */}
            <section className="relative bg-ink text-white overflow-hidden">
                <div className="absolute -right-20 -top-20 w-96 h-96 bg-splash/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -left-20 bottom-0 w-72 h-72 bg-cobalt/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-14 sm:pt-16 sm:pb-20">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.25em] text-splash mb-4 inline-flex items-center gap-2">
                        <TrendUp size={12} weight="bold" /> Free salary benchmark
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-4">
                        Are you being <br className="sm:hidden" />
                        <span className="text-splash">underpaid?</span>
                    </h1>
                    <p className="text-base sm:text-lg text-white/70 max-w-xl mb-8 leading-relaxed">
                        See the real 25/50/75 percentile salary for your role, years, and city — sourced from
                        blended 2025 India market data. Takes 15 seconds.
                    </p>

                    <form
                        onSubmit={submit}
                        data-testid="salary-form"
                        className="bg-white text-ink p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 max-w-4xl shadow-2xl"
                    >
                        <div className="sm:col-span-2 relative">
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                                Your role
                            </label>
                            <div className="flex items-center gap-2 border border-[#E5E5E5] px-3">
                                <MagnifyingGlass size={14} weight="bold" className="text-[#A3A3A3]" />
                                <input
                                    value={role}
                                    onChange={(e) => { setRole(e.target.value); setShowRoleDropdown(true); }}
                                    onFocus={() => setShowRoleDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowRoleDropdown(false), 150)}
                                    placeholder="e.g. Backend Engineer"
                                    data-testid="salary-input-role"
                                    className="w-full bg-transparent py-3 text-sm focus:outline-none placeholder:text-[#A3A3A3]"
                                />
                            </div>
                            {showRoleDropdown && filteredRoles.length > 0 && (
                                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E5E5] shadow-lg z-30 max-h-64 overflow-auto">
                                    {filteredRoles.map((r) => (
                                        <button
                                            type="button"
                                            key={r.key}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => { setRole(r.label); setShowRoleDropdown(false); }}
                                            data-testid={`salary-role-suggestion-${r.key}`}
                                            className="block w-full text-left px-3 py-2 text-sm hover:bg-splash/10"
                                        >
                                            {r.label}
                                            <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] ml-2">{r.category}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                                Years exp
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="40"
                                step="0.5"
                                value={years}
                                onChange={(e) => setYears(e.target.value)}
                                data-testid="salary-input-years"
                                className="w-full border border-[#E5E5E5] px-3 py-3 text-sm focus:outline-none focus:border-ink"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                                City
                            </label>
                            <div className="relative">
                                <MapPin size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                                <select
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    data-testid="salary-input-city"
                                    className="w-full border border-[#E5E5E5] pl-8 pr-3 py-3 text-sm bg-white focus:outline-none focus:border-ink"
                                >
                                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="sm:col-span-4">
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="salary-submit"
                                className="btn-gradient w-full sm:w-auto px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {loading ? "Estimating…" : (<><ChartLineUp size={14} weight="bold" /> Show my benchmark</>)}
                            </button>
                        </div>
                    </form>

                    {/* Trust signals */}
                    <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-10 max-w-2xl">
                        {[
                            ["Data points", "25k+"],
                            ["Roles covered", `${roles.length || 25}+`],
                            ["Cities", "12"],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <p className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white">
                                    {value}
                                </p>
                                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white/50 mt-1">
                                    {label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Result */}
            {result && (
                <section id="salary-result" data-testid="salary-result" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                    <div className="bg-white border border-[#E5E5E5] p-6 sm:p-10 shadow-sm">
                        <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                                    Your benchmark
                                </p>
                                <h2 className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                                    {result.role.label} · {result.years} yr · {result.city}
                                </h2>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                                    Median (P50)
                                </p>
                                <p className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-ink">
                                    {fmtINR(result.p50)}
                                </p>
                            </div>
                        </div>

                        <PercentileBar p25={result.p25} p50={result.p50} p75={result.p75} p90={result.p90} />

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-12">
                            {[
                                ["Bottom 25%", result.p25, "text-[#525252]", "p25"],
                                ["Median", result.p50, "text-cobalt", "p50"],
                                ["Top 25%", result.p75, "text-splash", "p75"],
                                ["Top 10%", result.p90, "text-emerald-600", "p90"],
                            ].map(([label, v, tone, key]) => (
                                <div key={label} data-testid={`salary-tile-${key}`} className="border border-[#E5E5E5] p-4">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                                        {label}
                                    </p>
                                    <p className={`font-heading text-xl sm:text-2xl font-black tracking-tight ${tone} mt-1`}>
                                        {fmtINR(v)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <p className="text-[11px] text-[#A3A3A3] mt-6 flex items-center gap-1.5">
                            <Sparkle size={11} weight="fill" />
                            Based on {result.sample_size}+ reported salaries · {result.source}
                        </p>

                        {/* Actionable insight */}
                        <div className="mt-6 p-4 bg-splash/10 border-l-4 border-splash">
                            <p className="text-[11px] font-black uppercase tracking-wider text-ink mb-1 flex items-center gap-1.5">
                                <Lightbulb size={12} weight="fill" />
                                Insight
                            </p>
                            <p className="text-sm text-ink leading-relaxed">
                                To land in the top 25% ({fmtINR(result.p75)}+), most {result.role.label}s in {result.city} highlight
                                measurable impact (revenue, latency, users) on their CV and add 2-3 recent certifications.
                                A strong CV is what gets you past the ATS filter.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                            <Link
                                to="/rate-my-cv"
                                data-testid="salary-cta-rate-cv"
                                className="btn-gradient px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
                            >
                                <Sparkle size={14} weight="bold" />
                                Get my CV rated (Free)
                            </Link>
                            <Link
                                to="/jobs"
                                data-testid="salary-cta-jobs"
                                className="bg-ink hover:bg-cobalt text-white px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
                            >
                                <Buildings size={14} weight="bold" />
                                Browse open roles
                            </Link>
                        </div>

                        {/* Email capture */}
                        {!leadCaptured ? (
                            <form
                                onSubmit={submitLead}
                                data-testid="salary-lead-form"
                                className="mt-8 p-5 bg-ink text-white flex flex-col sm:flex-row gap-3 items-start sm:items-center"
                            >
                                <div className="flex-1">
                                    <p className="font-heading text-lg font-black tracking-tight">
                                        Get the weekly salary trends report
                                    </p>
                                    <p className="text-[11px] text-white/60 mt-1">
                                        Real hiring benchmarks + role-specific alerts. No spam, unsubscribe any time.
                                    </p>
                                </div>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@work.com"
                                    data-testid="salary-lead-email"
                                    className="bg-white text-ink w-full sm:w-64 px-3 py-2.5 text-sm focus:outline-none"
                                />
                                <button
                                    type="submit"
                                    data-testid="salary-lead-submit"
                                    className="bg-splash text-ink hover:bg-splash/90 w-full sm:w-auto px-5 py-2.5 text-xs font-black uppercase tracking-wider transition-colors"
                                >
                                    Subscribe
                                </button>
                            </form>
                        ) : (
                            <div className="mt-8 p-5 bg-emerald-50 border border-emerald-200 flex items-center gap-3" data-testid="salary-lead-success">
                                <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
                                <p className="text-sm text-ink">
                                    You&apos;re on the list. Look for our weekly trends email starting next Monday.
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Why trust us */}
            <RolesHiringStrip
                eyebrow="LIVE HIRING · ACROSS AGENCIES"
                headline="Roles paying this range · hiring right now."
                subhead="Real agency mandates from NaukriDaddy's Talent Marketplace. Match your profile once, apply everywhere."
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-3">
                    Why this benchmark works
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl mb-8">
                    Blended market data, not one company&apos;s payroll.
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                        {
                            title: "Real 2025 numbers",
                            copy: "Built from 25,000+ reported salaries across Naukri, AmbitionBox, Payscale + our own hires.",
                        },
                        {
                            title: "City-adjusted",
                            copy: "Bengaluru, Mumbai, Gurugram pay differently. We adjust for cost-of-hire multipliers, not just cost-of-living.",
                        },
                        {
                            title: "Percentile-first",
                            copy: "Averages lie. We show P25, P50, P75, P90 so you know exactly where you stand — and what to target.",
                        },
                    ].map((c) => (
                        <div key={c.title} className="bg-white border border-[#E5E5E5] p-5">
                            <p className="font-heading text-lg font-bold tracking-tight mb-2">{c.title}</p>
                            <p className="text-sm text-[#525252] leading-relaxed">{c.copy}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer CTA */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
                <div className="bg-ink text-white p-6 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-2">
                            Next step
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                            Know your worth?
                            <br className="hidden sm:inline" /> Now land the interview.
                        </h2>
                    </div>
                    <Link
                        to="/rate-my-cv"
                        data-testid="salary-footer-cta"
                        className="btn-gradient px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                        Rate my CV — Free <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
